import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defRoute, LoadedProps } from '@components/router';
import { useState } from 'preact/hooks';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { IcoPlus, IcoTrash } from '@components/icons';
import { showDialog } from '@components/dialog';
import { showError } from '@components/app-error';
import { hasLevel } from 'shared/auth';
import { useCurrentUser } from '@components/router/session-context';

type Entry = RpxResponse<typeof rpx.changelog.getAllEntries>[0];

const CATEGORY_STYLES: Record<string, { bg: string; label: string }> = {
  feature: { bg: 'bg-blue-100 text-blue-700', label: 'Feature' },
  improvement: { bg: 'bg-green-100 text-green-700', label: 'Improvement' },
  fix: { bg: 'bg-amber-100 text-amber-700', label: 'Fix' },
  design: { bg: 'bg-purple-100 text-purple-700', label: 'Design' },
};

function CategoryBadge({ category }: { category: string }) {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.improvement;
  return (
    <span class={`text-xs px-2 py-0.5 rounded-full font-medium ${style.bg}`}>{style.label}</span>
  );
}

function formatDate(date: Date | string) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMonthYear(date: Date | string) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function groupByMonth(entries: Entry[]): Map<string, Entry[]> {
  const groups = new Map<string, Entry[]>();
  for (const entry of entries) {
    const key = formatMonthYear(entry.createdAt);
    const existing = groups.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return groups;
}

async function load() {
  const entries = await rpx.changelog.getAllEntries();
  return { entries };
}

type Props = LoadedProps<typeof load>;

export const route = defRoute({
  authLevel: 'admin',
  load,
  Page,
});

function EntryCard({
  entry,
  isSuperadmin,
  onUpdate,
  onDelete,
}: {
  entry: Entry;
  isSuperadmin: boolean;
  onUpdate: (entry: Entry) => void;
  onDelete: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(entry.title);
  const [description, setDescription] = useState(entry.description);
  const [category, setCategory] = useState(entry.category);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSave() {
    setIsSaving(true);
    try {
      const updated = await rpx.changelog.updateEntry({
        id: entry.id,
        title,
        description,
        category: category as 'feature' | 'improvement' | 'fix' | 'design',
      });
      onUpdate(updated as unknown as Entry);
      setIsEditing(false);
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    const confirmed = await showDialog({
      mode: 'warn',
      title: 'Delete changelog entry?',
      children: 'This will permanently delete this entry.',
      confirmButtonText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await rpx.changelog.deleteEntry({ id: entry.id });
      onDelete(entry.id);
    } catch (err) {
      showError(err as Error);
    }
  }

  if (isEditing) {
    return (
      <div class="bg-white border rounded-lg p-4 flex flex-col gap-3">
        <input
          class="ruz-input text-sm font-medium"
          value={title}
          onInput={(e: any) => setTitle(e.target.value)}
          placeholder="Title"
        />
        <textarea
          class="ruz-input text-sm min-h-24"
          value={description}
          onInput={(e: any) => setDescription(e.target.value)}
          placeholder="Description (supports plain text)"
        />
        <select
          class="ruz-input text-sm w-48"
          value={category}
          onChange={(e: any) => setCategory(e.target.value)}
        >
          <option value="feature">Feature</option>
          <option value="improvement">Improvement</option>
          <option value="fix">Fix</option>
          <option value="design">Design</option>
        </select>
        <div class="flex gap-2">
          <BtnPrimary class="text-sm" onClick={handleSave} disabled={isSaving}>
            Save
          </BtnPrimary>
          <BtnSecondary class="text-sm" onClick={() => setIsEditing(false)}>
            Cancel
          </BtnSecondary>
        </div>
      </div>
    );
  }

  return (
    <div class="bg-white border rounded-lg p-4 flex flex-col gap-2">
      <div class="flex items-start gap-3">
        <div class="flex flex-col gap-1 grow">
          <div class="flex items-center gap-2">
            <CategoryBadge category={entry.category} />
            <span class="text-xs text-gray-400">{formatDate(entry.createdAt)}</span>
          </div>
          <h3 class="font-medium text-gray-900">{entry.title}</h3>
          {entry.description && (
            <p class="text-sm text-gray-600 whitespace-pre-wrap">{entry.description}</p>
          )}
        </div>
        {isSuperadmin && (
          <div class="flex items-center gap-1 shrink-0">
            <button
              class="text-xs px-2 py-1 rounded hover:bg-gray-100 text-gray-500"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
            <button
              class="text-xs px-2 py-1 rounded hover:bg-gray-100 text-red-500"
              onClick={handleDelete}
            >
              <IcoTrash class="size-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateEntryForm({ onCreated }: { onCreated: (entry: Entry) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('improvement');
  const [isCreating, setIsCreating] = useState(false);

  async function handleCreate() {
    if (!title) {
      return;
    }
    setIsCreating(true);
    try {
      const entry = await rpx.changelog.createEntry({
        title,
        description,
        category: category as 'feature' | 'improvement' | 'fix' | 'design',
      });
      onCreated(entry as unknown as Entry);
      setTitle('');
      setDescription('');
      setCategory('improvement');
      setIsOpen(false);
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsCreating(false);
    }
  }

  if (!isOpen) {
    return (
      <BtnPrimary class="gap-1 text-sm" onClick={() => setIsOpen(true)}>
        <IcoPlus />
        New Entry
      </BtnPrimary>
    );
  }

  return (
    <div class="bg-white border rounded-lg p-4 flex flex-col gap-3">
      <p class="text-sm font-medium text-gray-700">New Changelog Entry</p>
      <input
        class="ruz-input text-sm"
        value={title}
        onInput={(e: any) => setTitle(e.target.value)}
        placeholder="What changed?"
      />
      <textarea
        class="ruz-input text-sm min-h-20"
        value={description}
        onInput={(e: any) => setDescription(e.target.value)}
        placeholder="Details (optional)"
      />
      <select
        class="ruz-input text-sm w-48"
        value={category}
        onChange={(e: any) => setCategory(e.target.value)}
      >
        <option value="feature">Feature</option>
        <option value="improvement">Improvement</option>
        <option value="fix">Fix</option>
        <option value="design">Design</option>
      </select>
      <div class="flex gap-2">
        <BtnPrimary class="text-sm" onClick={handleCreate} disabled={isCreating || !title}>
          Save
        </BtnPrimary>
        <BtnSecondary class="text-sm" onClick={() => setIsOpen(false)}>
          Cancel
        </BtnSecondary>
      </div>
    </div>
  );
}

function Page(props: Props) {
  const user = useCurrentUser();
  const isSuperadmin = hasLevel(user, 'superadmin');
  const [entries, setEntries] = useState(props.state.entries);

  const visibleEntries = entries;
  const grouped = groupByMonth(visibleEntries);

  function handleUpdate(updated: Entry) {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  }

  function handleDelete(id: string) {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  function handleCreated(entry: Entry) {
    setEntries((prev) => [entry, ...prev]);
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="changelog" pageTitle="Changelog" />
        <AdminTabWrapper>
          <AdminTabHeader title="Changelog">
            {isSuperadmin && <CreateEntryForm onCreated={handleCreated} />}
          </AdminTabHeader>

          {visibleEntries.length === 0 && (
            <p class="text-gray-500 text-center py-12">No changelog entries yet.</p>
          )}

          <div class="flex flex-col gap-8">
            {Array.from(grouped.entries()).map(([month, monthEntries]) => (
              <div key={month}>
                <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  {month}
                </h3>
                <div class="flex flex-col gap-3">
                  {monthEntries.map((entry) => (
                    <EntryCard
                      key={entry.id}
                      entry={entry}
                      isSuperadmin={isSuperadmin}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
